import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { Sparkles, Zap, Plus, Trash2, FlaskConical } from "lucide-react";

const inp = "h-9 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const PINK = "#C4738A";

export default function AdminAISettings() {
  const [s, setS] = useState(null);
  const [usage, setUsage] = useState(null);
  const [rules, setRules] = useState([]);
  const [cats, setCats] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [sim, setSim] = useState({ category: "", modal: "" });
  const [simResult, setSimResult] = useState(null);

  const load = () => {
    api.get("/admin/ai/settings").then((r) => setS(r.data)).catch(() => setS(false));
    api.get("/admin/ai/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/admin/pricing-rules").then((r) => setRules(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      await api.put("/admin/ai/settings", { enabled: s.enabled, doc_model: s.doc_model, image_model: s.image_model, monthly_limit: parseInt(s.monthly_limit) || 200, auto_pricing: s.auto_pricing !== false, auto_sku: s.auto_sku !== false, auto_barcode: s.auto_barcode !== false });
      toast.success("Pengaturan AI tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/ai/test", {}, { timeout: 60000 });
      setTestResult({ ok: true, text: `Terhubung — model ${data.model} merespons: "${data.response}"` });
    } catch (e) { setTestResult({ ok: false, text: e?.response?.data?.detail || "Koneksi gagal" }); }
    finally { setTesting(false); }
  };

  const addRule = () => setRules([...rules, { id: null, category: "", modal_min: 0, modal_max: 100000, mode: "profit", sell_price: 0, profit: 5000, rounding: 1000, active: true, _new: true }]);
  const setR = (i, k, v) => setRules((rs) => rs.map((r, x) => (x === i ? { ...r, [k]: v } : r)));
  const saveRule = async (r, i) => {
    try {
      const payload = {
        category: r.category, modal_min: parseInt(r.modal_min) || 0, modal_max: parseInt(r.modal_max) || 0,
        mode: r.mode || "fixed", sell_price: parseInt(r.sell_price) || 0,
        profit: r.profit === "" || r.profit == null ? null : parseInt(r.profit),
        rounding: parseInt(r.rounding) || 0, active: !!r.active,
      };
      if (r._new) await api.post("/admin/pricing-rules", payload);
      else await api.put(`/admin/pricing-rules/${r.id}`, payload);
      toast.success("Aturan tersimpan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan aturan"); }
  };
  const delRule = async (r) => {
    if (r._new) return setRules(rules.filter((x) => x !== r));
    try { await api.delete(`/admin/pricing-rules/${r.id}`); toast.success("Aturan dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const simulate = async () => {
    if (!sim.modal) return toast.error("Isi harga modal dulu");
    try {
      const { data } = await api.post("/admin/pricing-rules/simulate", { category: sim.category, modal: parseInt(sim.modal) });
      setSimResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulasi gagal"); }
  };

  if (s === null) return <div className="py-20 text-center text-xs text-slate-400">Memuat...</div>;
  if (s === false) return <div className="py-20 text-center text-xs text-slate-400">Halaman ini khusus owner/admin.</div>;

  return (
    <div data-testid="ai-settings-page" className="max-w-4xl space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">AI & Aturan Harga</h1>
        <p className="text-xs text-slate-400">Integrasi OpenAI untuk Tambah Produk AI + aturan harga jual otomatis berdasarkan harga modal</p>
      </div>

      {/* Integrasi AI */}
      <section className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-xl bg-rose-50 text-[#C4738A] flex items-center justify-center"><Sparkles className="w-4 h-4" /></span>
          <div className="flex-1">
            <h2 className="font-display font-bold text-sm text-slate-900">Integrasi AI (OpenAI)</h2>
            <p className="text-[10px] text-slate-400">API key tersimpan aman di server — tidak pernah ditampilkan atau dikirim ke browser</p>
          </div>
          <span data-testid="ai-key-status" className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${s.has_key ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
            {s.has_key ? "API Key Terpasang" : "API Key Belum Ada"}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Model Analisis Dokumen</label>
            <select data-testid="ai-doc-model" value={s.doc_model} onChange={(e) => setS({ ...s, doc_model: e.target.value })} className={`${inp} w-full`}>
              {s.doc_models.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Model Generate Foto <span className="text-slate-400 font-normal">(untuk fase foto AI)</span></label>
            <select data-testid="ai-image-model" value={s.image_model} onChange={(e) => setS({ ...s, image_model: e.target.value })} className={`${inp} w-full`}>
              {s.image_models.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Batas Penggunaan per Bulan (kali parse)</label>
            <input data-testid="ai-limit" type="number" min="1" value={s.monthly_limit} onChange={(e) => setS({ ...s, monthly_limit: e.target.value })} className={`${inp} w-full`} />
          </div>
          <div className="flex items-end gap-6">
            <label className="flex items-center gap-2 text-xs text-slate-700 font-medium h-9">
              <input data-testid="ai-enabled" type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="w-4 h-4 rounded border-slate-300 accent-[#C4738A]" />
              Fitur AI Aktif
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-700 font-medium h-9">
              <input data-testid="ai-auto-pricing-set" type="checkbox" checked={s.auto_pricing !== false} onChange={(e) => setS({ ...s, auto_pricing: e.target.checked })} className="w-4 h-4 rounded border-slate-300 accent-[#C4738A]" />
              Harga jual otomatis per kategori saat generate
            </label>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="ai-settings-save" onClick={saveSettings} className="h-10 px-5 rounded-xl bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold transition-colors">Simpan Pengaturan</button>
          <button data-testid="ai-test-btn" onClick={testConn} disabled={testing}
            className="h-10 px-5 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors disabled:opacity-50">
            <Zap className="w-3.5 h-3.5" /> {testing ? "Menguji..." : "Tes Koneksi"}
          </button>
          {testResult && (
            <span data-testid="ai-test-result" className={`text-[11px] font-medium ${testResult.ok ? "text-emerald-600" : "text-red-500"}`}>{testResult.text}</span>
          )}
        </div>
        {usage && (
          <div className="rounded-xl bg-slate-50 p-4">
            <div className="text-[11px] font-semibold text-slate-600 mb-2">Riwayat & Penggunaan — bulan ini: <b>{usage.month_count}</b> / {usage.monthly_limit} panggilan</div>
            <div className="max-h-36 overflow-y-auto space-y-1">
              {usage.items.slice(0, 10).map((u) => (
                <div key={u.id} className="text-[10px] text-slate-500 flex justify-between gap-3">
                  <span>{u.kind} • {u.model} • {u.detail}</span>
                  <span className="text-slate-400 flex-shrink-0">{(u.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
              {!usage.items.length && <div className="text-[10px] text-slate-400">Belum ada penggunaan.</div>}
            </div>
          </div>
        )}
      </section>

      {/* Aturan Harga Jual */}
      <section className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-sm text-slate-900">Aturan Harga Jual</h2>
        <p className="text-[10px] text-slate-400">Harga jual otomatis draft AI dihitung dari aturan <b>modal + profit per kategori</b> (dengan pembulatan 500/1.000) yang Anda atur di bawah — aktif/nonaktif lewat toggle di halaman Tambah Produk AI atau pengaturan di atas.</p>
          </div>
          <button data-testid="rule-add" onClick={addRule} className="h-9 px-4 rounded-xl border border-[#C4738A]/40 text-[#C4738A] text-xs font-semibold flex items-center gap-1.5 hover:bg-[#C4738A]/5 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Tambah Aturan
          </button>
        </div>
        <div className="rounded-xl border border-slate-100 overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
                <th className="text-left px-3 py-2.5 font-semibold">Kategori</th>
                <th className="text-right px-3 py-2.5 font-semibold">Modal Min</th>
                <th className="text-right px-3 py-2.5 font-semibold">Modal Maks</th>
                <th className="text-left px-3 py-2.5 font-semibold">Mode</th>
                <th className="text-right px-3 py-2.5 font-semibold">Profit / Harga</th>
                <th className="text-left px-3 py-2.5 font-semibold">Pembulatan</th>
                <th className="text-center px-3 py-2.5 font-semibold">Aktif</th>
                <th className="px-3 py-2.5 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rules.map((r, i) => (
                <tr key={r.id || `new-${i}`} data-testid={`rule-row-${i}`}>
                  <td className="px-3 py-2">
                    <select data-testid={`rule-cat-${i}`} value={r.category} onChange={(e) => setR(i, "category", e.target.value)} className={inp}>
                      <option value="">Semua Kategori</option>
                      {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2"><input data-testid={`rule-min-${i}`} type="number" value={r.modal_min} onChange={(e) => setR(i, "modal_min", e.target.value)} className={`${inp} text-right w-24 ml-auto`} /></td>
                  <td className="px-3 py-2"><input data-testid={`rule-max-${i}`} type="number" value={r.modal_max} onChange={(e) => setR(i, "modal_max", e.target.value)} className={`${inp} text-right w-24 ml-auto`} /></td>
                  <td className="px-3 py-2">
                    <select data-testid={`rule-mode-${i}`} value={r.mode || "fixed"} onChange={(e) => setR(i, "mode", e.target.value)} className={`${inp} w-36`}>
                      <option value="profit">Modal + Profit</option>
                      <option value="fixed">Harga Tetap</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    {(r.mode || "fixed") === "profit"
                      ? <input data-testid={`rule-profit-${i}`} type="number" value={r.profit ?? ""} onChange={(e) => setR(i, "profit", e.target.value)} placeholder="Profit" className={`${inp} text-right w-24 ml-auto`} />
                      : <input data-testid={`rule-sell-${i}`} type="number" value={r.sell_price} onChange={(e) => setR(i, "sell_price", e.target.value)} placeholder="Harga tetap" className={`${inp} text-right w-24 ml-auto`} />}
                  </td>
                  <td className="px-3 py-2">
                    <select data-testid={`rule-rounding-${i}`} value={r.rounding ?? 1000} onChange={(e) => setR(i, "rounding", e.target.value)} disabled={(r.mode || "fixed") !== "profit"} className={`${inp} w-28 disabled:opacity-40`}>
                      <option value="0">Tanpa</option>
                      <option value="500">Ke 500</option>
                      <option value="1000">Ke 1.000</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center"><input data-testid={`rule-active-${i}`} type="checkbox" checked={!!r.active} onChange={(e) => setR(i, "active", e.target.checked)} className="w-4 h-4 rounded border-slate-300 accent-[#C4738A]" /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button data-testid={`rule-save-${i}`} onClick={() => saveRule(r, i)} className="h-8 px-3 rounded-lg bg-slate-800 text-white text-[10px] font-semibold hover:bg-slate-900">Simpan</button>
                      <button data-testid={`rule-del-${i}`} onClick={() => delRule(r)} className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!rules.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-xs text-slate-400">Belum ada aturan — harga jual draft AI akan kosong sampai aturan dibuat atau diisi manual.</td></tr>}
            </tbody>
          </table>
        </div>
        {/* Simulasi */}
        <div className="rounded-xl bg-slate-50 p-4 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 mb-1">Simulasi — Kategori</label>
            <select data-testid="sim-cat" value={sim.category} onChange={(e) => setSim({ ...sim, category: e.target.value })} className={inp}>
              <option value="">Semua Kategori</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 mb-1">Harga Modal</label>
            <input data-testid="sim-modal" type="number" value={sim.modal} onChange={(e) => setSim({ ...sim, modal: e.target.value })} placeholder="25000" className={`${inp} w-36`} />
          </div>
          <button data-testid="sim-run" onClick={simulate} className="h-9 px-4 rounded-lg bg-slate-800 text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-900">
            <FlaskConical className="w-3.5 h-3.5" /> Simulasikan
          </button>
          {simResult && (
            <span data-testid="sim-result" className="text-xs font-semibold text-slate-700">
              {simResult.matched
                ? <>→ Harga jual: <span className="text-[#C4738A]">{formatIDR(simResult.sell_price)}</span>{simResult.detail ? <span className="text-slate-400 font-normal"> ({simResult.detail})</span> : null}</>
                : "→ Tidak ada aturan yang cocok"}
            </span>
          )}
        </div>
      </section>
    </div>
  );
}
